import { Check, MapPin, Mail, Phone, Timer, UserRound } from 'lucide-react';
import InquiryJourney, { Field, GroupTiles, HowDidYouHear, InterestTiles, INTEREST_GROUPS, INTEREST_PHOTO, OTHER_MAX, Privacy } from './InquiryJourney';
import { PHONE_INPUT } from '../lib/formRules';
import { publicHref } from '../lib/publicUrls';
import { getAttribution } from '../lib/attribution';

// The standard request journey (welcome → interests → name → contact → review) used by Book a
// Tour and every program inquiry. It sends exactly what the previous inquiry form sent: name,
// email, phone, location, interests (starting from `defaultInterests`), the form's `source`,
// campaign attribution and the how-did-you-hear answer. Name, email, phone and how-did-you-hear
// are required (owner direction, Sept 30).
//
// `guided` (owner direction, Sept 30; Book a Tour first) is the simpler version for visitors who
// aren't comfortable with forms: no welcome screen, one short interest question per screen, each
// with "Other" and a text box, then one screen for name and contact, then the review. Anything typed
// under "Other" travels with the interests, e.g. "Other (Train): rock climbing", so the team sees it.

// Guided screens: the group's name as the question, in plain words.
const GUIDED_GROUPS = {
  Train: ['Train', 'Pick as many as you like, or skip.'],
  Classes: ['Classes', 'Any group classes you’d like to try?'],
  'Pool & family': ['Pool & Family', 'Swimming, swim lessons or the Kid’s Club?'],
  'Play & recover': ['Play & Recover', 'Courts and massage. Pick any, or skip.'],
};
const otherInterests = (others = {}) => Object.entries(others)
  .map(([group, text]) => (text.trim() ? `Other (${GUIDED_GROUPS[group][0]}): ${text.trim().slice(0, OTHER_MAX)}` : `Other (${GUIDED_GROUPS[group][0]})`));

export const CLUB_ADDRESS = '120 Guthrie Ln., Brentwood, CA 94513';
const DEFAULT_PROMISES = [[Timer, 'About a minute'], [Check, 'No commitment'], [UserRound, 'A real person follows up']];

export default function ProgramJourney({
  id, tone = 'blue', testId = 'program-journey', welcomeLevel = 'h2', progressLabel = 'Request steps',
  source, location = 'Brentwood', defaultInterests = [],
  eyebrow, title, lede, promises = DEFAULT_PROMISES, note, photo = null,
  request, planTitle = 'Your request',
  interestsStep = 'Interests', interestsQuestion = 'Anything else you’d like to hear about?',
  interestsLede = 'Pick anything you’re curious about, or skip ahead. It helps the right person follow up.',
  interestsLabel = 'Interested in', interestsEmpty = 'Just this for now', skipLabel = 'Skip for Now',
  nameQuestion = 'Who should we ask for?', nameLede = 'So our team knows who they’re talking to.', nameLabel = 'Name',
  reachLede = 'We’ll only use this to follow up on your request.',
  reviewNoun = 'request', reviewLede = 'Take a look, then send it to our team.',
  startLabel = 'Get Started', buttonText, successTitle = 'Request Received', successHeading = 'You’re all set', successMessage,
  successNext, successActions, guided = false, breakdown,
}) {
  const chosenOf = (form) => form.interests.filter((item) => INTEREST_PHOTO[item] !== undefined);
  const lastChosen = (form) => chosenOf(form).slice(-1)[0];
  const fullName = (form) => `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
  const reachAt = (form) => [form.email, form.phone].filter(Boolean).join(' · ');
  const withName = (text, first) => `${text}${first ? `, ${first}` : ''}`;
  const wanted = (form) => [...chosenOf(form), ...otherInterests(form.others).map((item) => item.replace(/^Other \([^)]*\)/, 'Other'))];

  const nameFields = ({ form, fieldId, invalid, update }) => (
    <div className="dv-journey__fields dv-journey__fields--pair">
      <Field id={fieldId('firstName')} label="First Name *" icon={UserRound} invalid={invalid('firstName')}
        input={{ type: 'text', placeholder: 'Your first name', autoComplete: 'given-name', required: true, value: form.firstName, onChange: update('firstName') }} />
      <Field id={fieldId('lastName')} label="Last Name *" invalid={invalid('lastName')}
        input={{ type: 'text', placeholder: 'Your last name', autoComplete: 'family-name', required: true, value: form.lastName, onChange: update('lastName') }} />
    </div>
  );
  const contactFields = ({ form, fieldId, invalid, update }) => (
    <div className="dv-journey__fields">
      <Field id={fieldId('email')} label="Email *" icon={Mail} invalid={invalid('email')}
        input={{ type: 'email', placeholder: 'name@email.com', autoComplete: 'email', required: true, value: form.email, onChange: update('email') }} />
      <Field id={fieldId('phone')} label="Phone *" icon={Phone} invalid={invalid('phone')}
        input={{ ...PHONE_INPUT, placeholder: '(925) 555-0123', value: form.phone, onChange: update('phone') }} />
    </div>
  );

  const guidedSteps = [
    ...INTEREST_GROUPS.map(([group, items]) => {
      const names = items.map(([name]) => name);
      const lastInGroup = (form) => form.interests.filter((item) => names.includes(item)).slice(-1)[0];
      const [question, lede] = GUIDED_GROUPS[group];
      return {
        label: question,
        question: () => question,
        lede,
        render: (ctx) => <GroupTiles ctx={ctx} group={group} items={items} labelledBy={ctx.titleId} />,
        photo: ({ preview, form }) => INTEREST_PHOTO[preview ?? lastInGroup(form) ?? names.find((name) => INTEREST_PHOTO[name])],
        caption: ({ preview, form }) => preview ?? lastInGroup(form),
        nextLabel: ({ form }) => (form.interests.some((item) => names.includes(item)) || form.others[group] !== undefined ? 'Next' : 'Skip'),
      };
    }),
    {
      label: 'You',
      question: () => nameQuestion,
      lede: `${nameLede} ${reachLede}`,
      render: (ctx) => <>{nameFields(ctx)}{contactFields(ctx)}</>,
    },
  ];

  const steps = guided ? [...guidedSteps, null] : [
    {
      label: interestsStep,
      question: () => interestsQuestion,
      lede: interestsLede,
      render: (ctx) => <InterestTiles ctx={ctx} labelledBy={ctx.titleId} />,
      photo: ({ preview, form }) => INTEREST_PHOTO[preview ?? lastChosen(form)],
      caption: ({ preview, form }) => preview ?? lastChosen(form),
      nextLabel: ({ form }) => (chosenOf(form).length ? 'Continue' : skipLabel),
    },
    {
      label: 'You',
      question: () => nameQuestion,
      lede: nameLede,
      render: nameFields,
    },
    {
      label: 'Reach',
      question: ({ firstName }) => withName('How should we reach you', firstName) + '?',
      lede: reachLede,
      render: contactFields,
    },
    null,
  ];
  // The last screen is always the review.
  steps[steps.length - 1] = {
    label: 'Review',
    question: ({ firstName }) => withName(`Here’s your ${reviewNoun}`, firstName) + '.',
    lede: reviewLede,
    render: (ctx) => <><HowDidYouHear ctx={ctx} /><Privacy /></>,
    photo: ({ form }) => INTEREST_PHOTO[chosenOf(form)[0]],
  };
  // Screens to return to from the review (1-based): interests, name, contact.
  const youStep = guidedSteps.length;
  const [editInterests, editName, editReach] = guided ? [1, youStep, youStep] : [1, 2, 3];

  return (
    <InquiryJourney
      id={id} tone={tone} testId={testId} welcomeLevel={welcomeLevel} progressLabel={progressLabel}
      welcome={{ eyebrow, title, lede: guided ? breakdown : lede, promises, note, photo }}
      steps={steps}
      guided={guided}
      initial={{ firstName: '', lastName: '', email: '', phone: '', interests: defaultInterests, others: {}, howDidYouHear: '' }}
      missing={(form) => (!form.firstName || !form.lastName ? editName : !form.email || !form.phone ? editReach : -1)}
      payload={(form) => ({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        location,
        // The site's system keeps up to 20 interests per request.
        interests: [...form.interests, ...otherInterests(form.others)].slice(0, 20),
        source,
        ...getAttribution(),
        how_did_you_hear: form.howDidYouHear,
      })}
      plan={{
        title: planTitle,
        rows: ({ form }) => [
          ...(request ? [request] : []),
          [interestsLabel, wanted(form).join(' · ') || interestsEmpty, editInterests],
          [nameLabel, fullName(form), editName],
          ['Reach you at', reachAt(form), editReach],
        ],
      }}
      startLabel={startLabel}
      submitLabel={buttonText}
      success={{
        title: successTitle,
        heading: ({ firstName }) => withName(successHeading, firstName) + '.',
        lede: successMessage,
        photo: ({ form }) => INTEREST_PHOTO[chosenOf(form)[0]],
        next: successNext || [
          [Mail, 'We reach out', ({ form }) => `By email or phone, with the details you asked about.`],
          [Check, 'Ask anything', 'Current options, availability and next steps. No commitment.'],
          [MapPin, 'Come see us', CLUB_ADDRESS],
        ],
        actions: successActions || (
          <>
            <a className="dv-button dv-button--light" href={publicHref('/book-a-tour')}>Book a Tour</a>
            <a className="dv-button dv-button--outline-light" href={publicHref('/programs')}>Explore Programs</a>
          </>
        ),
      }}
    />
  );
}
